import {
  type Rgb,
  type ThinkingOrbGrain,
  ThinkingOrbHelper,
  type ThinkingOrbPhase,
} from './thinking-orb-helper';

interface OrbFrame {
  ctx: CanvasRenderingContext2D;
  size: number;
  c: number;
  R: number;
  t: number;
  ink: Rgb;
  deep: Rgb;
  phase: ThinkingOrbPhase;
  /** view rotation: the globe's spin and its fixed tilt toward the viewer */
  cy: number;
  sy: number;
  cxr: number;
  sxr: number;
}

interface RingPoint {
  X: number;
  Y: number;
  z: number;
}

interface WindMote {
  X: number;
  Y: number;
  PX: number;
  PY: number;
  z: number;
  life: number;
  weight: number;
}

interface PlacedGrain {
  X: number;
  Y: number;
  z: number;
  alpha: number;
  radius: number;
  light: number;
}

// brand-25 and brand-50, plus a mid sky blue between them and fg-brand
const BRAND_25: Rgb = [245, 250, 255];
const BRAND_50: Rgb = [231, 243, 254];
const SKY: Rgb = [120, 182, 236];

/** Draws one frame of the thinking orb onto a canvas. Pure apart from the canvas it is given. */
export class ThinkingOrbRenderer {
  private static readonly grainTextures = new Map<number, HTMLCanvasElement>();

  static drawFrame(
    ctx: CanvasRenderingContext2D,
    size: number,
    grains: readonly ThinkingOrbGrain[],
    T: number,
    t: number,
    ink: Rgb,
    deep: Rgb
  ): void {
    ctx.clearRect(0, 0, size, size);
    const ry = t * ThinkingOrbHelper.SPIN;
    const frame: OrbFrame = {
      ctx,
      size,
      c: size / 2,
      R: size * 0.4,
      t,
      ink,
      deep,
      phase: ThinkingOrbHelper.getPhase(T, t),
      cy: Math.cos(ry),
      sy: Math.sin(ry),
      cxr: Math.cos(-0.38),
      sxr: Math.sin(-0.38),
    };
    // below about 32px the rings stop reading, so small orbs drop them
    const rings = size >= 32 ? ThinkingOrbRenderer.getRings(frame) : [];
    const wind = ThinkingOrbRenderer.getWind(frame, grains.length);

    ThinkingOrbRenderer.drawRings(frame, rings, false);
    wind
      .filter((m) => m.z < 0)
      .forEach((m) => ThinkingOrbRenderer.drawMote(frame, m));
    const placed = grains
      .map((g) => ThinkingOrbRenderer.placeGrain(frame, g))
      .sort((p, q) => p.z - q.z);
    if (frame.phase.shade > 0.001)
      ThinkingOrbRenderer.drawGradientSphere(frame);
    placed.forEach((p) => {
      const col = ink.map((v, i) => Math.round(v + (deep[i] - v) * p.light));
      ctx.fillStyle = `rgba(${col.join(',')},${Math.min(1, p.alpha)})`;
      ctx.beginPath();
      ctx.arc(p.X, p.Y, p.radius, 0, Math.PI * 2);
      ctx.fill();
    });
    wind
      .filter((m) => m.z >= 0)
      .forEach((m) => ThinkingOrbRenderer.drawMote(frame, m));
    ThinkingOrbRenderer.drawRings(frame, rings, true);
  }

  /** Where a grain sits: packed in its dot (in the row or on the carousel), on the globe, or between. */
  private static placeGrain(f: OrbFrame, g: ThinkingOrbGrain): PlacedGrain {
    const { c, R, size, t, phase, cy, sy, cxr, sxr } = f;
    const H = ThinkingOrbHelper;
    const rd = Math.max(1.6, size * 0.04);
    const gap = rd * 4;

    // on the globe: grains slide slowly down and are replaced at the top
    const y = ((((g.y0 + 1 - t * 0.05 * g.drift) % 2) + 2) % 2) - 1;
    const rr = Math.sqrt(Math.max(0, 1 - y * y));
    const px = Math.cos(g.th) * rr;
    const pz = Math.sin(g.th) * rr;
    const x1 = px * cy + pz * sy;
    const z1 = -px * sy + pz * cy;
    const wy = y * cxr - z1 * sxr;
    const wz = y * sxr + z1 * cxr;
    const light = Math.exp(-((x1 - phase.bandX) ** 2) / 0.035) * phase.whole;
    const sc = (1 + 0.07 * light) * phase.swell * (1 + g.rough);
    const SX = c + R * sc * x1;
    const SY = c - R * sc * wy;

    // in a dot: which of the three, and where that dot is
    const k = Math.floor(g.tone * 3);
    let dotX = c + (k - 1) * gap * (1 - phase.merged);
    let dotY = c;
    let depthScale = 1;
    let dotZ = 1;
    let dotAlpha = 1;
    if (phase.revolving) {
      // perspective onto the carousel: near dots grow and drop, far ones shrink, rise and fade
      const base = [Math.PI, Math.PI / 2, 0][k];
      const even = [(7 * Math.PI) / 6, Math.PI / 2, -Math.PI / 6][k];
      const phi = base + (even - base) * phase.spread + phase.orbitTurn;
      const orbitR = gap * phase.orbitOpen;
      const z0 = orbitR * Math.sin(phi);
      const zT = z0 * Math.cos(phase.orbitTilt);
      const cam = gap * 3.2;
      const scale = cam / (cam - zT);
      dotX = c + orbitR * Math.cos(phi) * scale;
      dotY = c + z0 * Math.sin(phase.orbitTilt) * scale;
      depthScale = scale ** 1.3;
      const near = orbitR > 0 ? zT / orbitR : 0;
      dotZ = 1 + 0.5 * near;
      dotAlpha = 0.72 + (0.28 * (near + 1)) / 2;
    }
    const bounce =
      (1 - phase.still) *
      Math.max(0, Math.sin(t * 5 - k * 0.9)) ** 2 *
      rd *
      1.3;
    const pack = rd * (1 + 0.45 * phase.merged) * g.pack * depthScale;
    const ang = g.h * Math.PI * 2;
    const DX = dotX + pack * Math.cos(ang);
    const DY = dotY - bounce + pack * Math.sin(ang);

    // the breath carries every grain out together, so the globe forms as one movement
    const e = H.clamp01(
      phase.breath + 0.025 * (g.drift - 1) * Math.sin(Math.PI * phase.breath)
    );
    const es = e ** 0.4;
    const depth = (wz + 1) / 2;
    const life = H.smooth(-1, -0.88, y) * H.smooth(1, 0.985, y);
    const sandAlpha = (0.4 + 0.6 * depth ** 1.1) * life + 0.5 * light * life;
    const sandRadius =
      Math.max(0.3, size / 300) *
      g.grain *
      (0.6 + 0.6 * depth) *
      (1 + 0.5 * light);
    const dotRadius = Math.max(0.6, rd * 0.32);
    return {
      X: DX + (SX - DX) * e,
      Y: DY + (SY - DY) * e,
      z: wz * e + dotZ * (1 - e),
      // some of the sand stays visible on the gradient sphere, as texture
      alpha:
        (sandAlpha * es + dotAlpha * (1 - es)) *
        (1 - 0.3 * Math.sin(Math.PI * e)) *
        (1 - 0.65 * phase.shade),
      radius: sandRadius * es + dotRadius * (1 - es),
      light: Math.min(1, light),
    };
  }

  /** Soft brand-blue blobs drifting through the sphere, a deeper rim, the thinking light and grain. */
  private static drawGradientSphere(f: OrbFrame): void {
    const { ctx, c, R, t, ink, deep, phase } = f;
    // grows with the sand, so there is no pale halo ahead of it
    const Rs = R * phase.swell * Math.max(0.05, phase.breath);
    ctx.save();
    ctx.globalAlpha = phase.shade;
    ctx.beginPath();
    ctx.arc(c, c, Rs, 0, Math.PI * 2);
    ctx.clip();
    const fill = (style: CanvasGradient | string) => {
      ctx.fillStyle = style;
      ctx.fillRect(c - Rs, c - Rs, Rs * 2, Rs * 2);
    };
    fill(`rgb(${BRAND_25.join(',')})`);
    // each blob's path repeats a whole number of times per loop, so the loop is seamless
    const w = (2 * Math.PI) / ThinkingOrbHelper.LOOP;
    const blobs: {
      k: [number, number];
      ph: number;
      r: number;
      rgb: Rgb;
      a: number;
    }[] = [
      { k: [1, 2], ph: 0, r: 0.95, rgb: ink, a: 0.85 },
      { k: [2, 1], ph: 2.1, r: 0.8, rgb: deep, a: 0.75 },
      { k: [1, 3], ph: 4.2, r: 0.75, rgb: SKY, a: 0.8 },
      { k: [3, 2], ph: 1.3, r: 0.6, rgb: BRAND_50, a: 0.9 },
    ];
    blobs.forEach((b) => {
      const bx = c + Rs * 0.5 * Math.cos(w * b.k[0] * t + b.ph);
      const by = c + Rs * 0.5 * Math.sin(w * b.k[1] * t + b.ph * 1.7);
      const g = ctx.createRadialGradient(bx, by, 0, bx, by, Rs * b.r);
      g.addColorStop(0, `rgba(${b.rgb.join(',')},${b.a})`);
      g.addColorStop(1, `rgba(${b.rgb.join(',')},0)`);
      fill(g);
    });
    const rim = ctx.createRadialGradient(
      c - 0.15 * Rs,
      c - 0.2 * Rs,
      Rs * 0.55,
      c,
      c,
      Rs
    );
    rim.addColorStop(0, `rgba(${deep.join(',')},0)`);
    rim.addColorStop(1, `rgba(${deep.join(',')},0.45)`);
    fill(rim);
    if (phase.whole > 0.01 && phase.bandX < 2) {
      const bx = c + Rs * phase.bandX;
      const band = ctx.createLinearGradient(
        bx - Rs * 0.45,
        0,
        bx + Rs * 0.45,
        0
      );
      band.addColorStop(0, 'rgba(255,255,255,0)');
      band.addColorStop(0.5, `rgba(255,255,255,${0.38 * phase.whole})`);
      band.addColorStop(1, 'rgba(255,255,255,0)');
      fill(band);
    }
    const dpr = ctx.getTransform().a || 1;
    ctx.drawImage(
      ThinkingOrbRenderer.getGrainTexture(Math.round(f.size * dpr), deep),
      0,
      0,
      f.size,
      f.size
    );
    ctx.restore();
  }

  /** Fine film grain (light and deep-blue specks), made once per pixel size. */
  private static getGrainTexture(px: number, deep: Rgb): HTMLCanvasElement {
    const cached = ThinkingOrbRenderer.grainTextures.get(px);
    if (cached) return cached;
    const texture = document.createElement('canvas');
    texture.width = px;
    texture.height = px;
    const g = texture.getContext('2d');
    if (g) {
      const img = g.createImageData(px, px);
      for (let i = 0; i < img.data.length; i += 4) {
        const r = Math.random();
        if (r < 0.32) {
          img.data.set([255, 255, 255, 40 + Math.random() * 80], i);
        } else if (r < 0.5) {
          img.data.set([...deep, 30 + Math.random() * 60], i);
        }
      }
      g.putImageData(img, 0, 0);
    }
    ThinkingOrbRenderer.grainTextures.set(px, texture);
    return texture;
  }

  /**
   * Armillary rings that open with the breath: an equator and a polar meridian turn with the globe,
   * the ecliptic (tilted 23°) counter-turns once per loop.
   */
  private static getRings(f: OrbFrame): RingPoint[][] {
    const { c, R, t, phase, cy, sy, cxr, sxr } = f;
    if (phase.breath <= 0.01) return [];
    const rr = R * (1.06 + 0.18 * phase.breath);
    const tilt = 0.41;
    const back = (-2 * Math.PI * t) / ThinkingOrbHelper.LOOP;
    const shapes: ((u: number) => [number, number, number])[] = [
      (u) => [Math.cos(u), 0, Math.sin(u)],
      (u) => [0, Math.cos(u), Math.sin(u)],
      (u) => {
        const xt = Math.cos(u) * Math.cos(tilt);
        const z = Math.sin(u);
        return [
          xt * Math.cos(back) + z * Math.sin(back),
          -Math.cos(u) * Math.sin(tilt),
          -xt * Math.sin(back) + z * Math.cos(back),
        ];
      },
    ];
    return shapes.map((shape) =>
      Array.from({ length: 97 }, (_, i) => {
        const [x, y, z] = shape((i / 96) * Math.PI * 2);
        const x1 = x * cy + z * sy;
        const z1 = -x * sy + z * cy;
        return {
          X: c + rr * x1,
          Y: c - rr * (y * cxr - z1 * sxr),
          z: y * sxr + z1 * cxr,
        };
      })
    );
  }

  /** Back halves almost vanish behind the sphere; the thinking light brightens the rings it crosses. */
  private static drawRings(
    f: OrbFrame,
    rings: RingPoint[][],
    front: boolean
  ): void {
    const { ctx, c, R, ink, deep, phase } = f;
    if (!rings.length) return;
    ctx.lineWidth = Math.max(0.5, f.size / 150);
    ctx.lineCap = 'round';
    rings.forEach((points) => {
      points.forEach((p1, i) => {
        if (i === 0 || p1.z >= 0 !== front) return;
        const p0 = points[i - 1];
        const lit =
          Math.exp(-(((p1.X - c) / R - phase.bandX) ** 2) / 0.03) * phase.whole;
        const base = (front ? 0.5 : 0.08) * phase.breath;
        const col = ink.map((v, k) => Math.round(v + (deep[k] - v) * lit));
        ctx.strokeStyle = `rgba(${col.join(',')},${Math.min(1, base + (front ? 0.45 : 0.15) * lit)})`;
        ctx.beginPath();
        ctx.moveTo(p0.X, p0.Y);
        ctx.lineTo(p1.X, p1.Y);
        ctx.stroke();
      });
    });
  }

  /** A few loose grains lifted off the rim, swirling with short streaks, while the globe is whole. */
  private static getWind(f: OrbFrame, grainCount: number): WindMote[] {
    const { c, R, t, phase, cxr, sxr } = f;
    const { hash, SPIN } = ThinkingOrbHelper;
    const count = Math.round(grainCount * 0.03 * phase.whole);
    return Array.from({ length: count }, (_, j) => {
      const age = (((t / (2.5 + 3 * hash(j, 13)) + hash(j, 11)) % 1) + 1) % 1;
      const speed = (0.5 + 0.9 * hash(j, 12)) * (SPIN + 0.35);
      const rr = 1.04 + 0.5 * hash(j, 14) * age;
      const yy = (hash(j, 15) * 2 - 1) * 0.85 + (age - 0.5) * 0.4;
      const ring = rr * Math.sqrt(Math.max(0.15, 1 - yy * yy));
      const at = (angle: number) => {
        const z = ring * Math.sin(angle);
        return {
          x: ring * Math.cos(angle),
          y: yy * rr * cxr - z * sxr,
          z: yy * rr * sxr + z * cxr,
        };
      };
      const a0 = hash(j, 16) * Math.PI * 2 + t * speed;
      const now = at(a0);
      const prev = at(a0 - speed * 0.07);
      return {
        X: c + R * now.x,
        Y: c - R * now.y,
        PX: c + R * prev.x,
        PY: c - R * prev.y,
        z: now.z,
        life: Math.sin(Math.PI * age) * phase.whole,
        weight: hash(j, 17),
      };
    });
  }

  /** Over the face of the globe a mote stays faint; at the rim it shows. */
  private static drawMote(f: OrbFrame, mote: WindMote): void {
    const { ctx, c, R, ink } = f;
    const off = Math.hypot(mote.X - c, mote.Y - c) / R;
    const alpha =
      mote.life *
      (0.25 + (0.6 * (mote.z + 1)) / 2) *
      (mote.z >= 0
        ? 0.25 + 0.75 * ThinkingOrbHelper.smooth(0.85, 1.05, off)
        : 1);
    ctx.strokeStyle = `rgba(${ink.join(',')},${alpha})`;
    ctx.lineWidth = Math.max(
      0.4,
      Math.max(0.3, f.size / 300) * (0.8 + mote.weight)
    );
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(mote.PX, mote.PY);
    ctx.lineTo(mote.X, mote.Y);
    ctx.stroke();
  }
}
